import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, RefreshCw, Send, CalendarClock, Zap } from "lucide-react";
import { PageHeader, FormRow, KV } from "@/pages/Accounts";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const [proj, setProj] = useState(null);
  const [lists, setLists] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = async () => {
    try {
      const [p, l] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/contact-lists`),
      ]);
      setProj(p.data);
      setLists(l.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  // Poll parsing lists every 3s
  useEffect(() => {
    if (!lists) return;
    const parsingIds = lists.filter((l) => l.status === "parsing").map((l) => l.id);
    if (parsingIds.length === 0) return;
    const interval = setInterval(async () => {
      try {
        const l = await api.get(`/projects/${id}/contact-lists`);
        setLists(l.data);
      } catch (_e) {
        /* ignore */
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [lists, id]);

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? This will remove all contacts and the source file.`)) return;
    try {
      await api.delete(`/contact-lists/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild data-testid="project-back">
          <Link to="/projects">← Back to projects</Link>
        </Button>
      </div>
      <PageHeader
        title={proj?.name || "…"}
        description={proj?.description || "Project workspace"}
        action={
          <div className="flex gap-2">
            <Button variant="default" asChild data-testid="project-quicksend-link">
              <Link to="/quick-send">
                <Zap className="mr-2 h-4 w-4" />Quick Send
              </Link>
            </Button>
            <Button variant="outline" asChild data-testid="project-scheduled-link">
              <Link to={`/projects/${id}/scheduled`}>
                <CalendarClock className="mr-2 h-4 w-4" />Scheduled
              </Link>
            </Button>
            <Button variant="outline" asChild data-testid="project-campaigns-link">
              <Link to={`/projects/${id}/campaigns`}>
                <Send className="mr-2 h-4 w-4" />Campaigns
              </Link>
            </Button>
            <Button onClick={() => setWizardOpen(true)} data-testid="project-upload-open">
              <Upload className="mr-2 h-4 w-4" />
              Upload contacts (CSV/XLSX)
            </Button>
          </div>
        }
      />

      <Card data-testid="contact-lists-card">
        <CardHeader>
          <CardTitle className="text-base">Contact lists</CardTitle>
          <CardDescription>
            Upload your customer databases. Filter, dedupe, and prepare them for Phase 3 campaigns.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Rows (valid / total)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Filterable cols</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lists === null && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="inline h-4 w-4 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {lists?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-xs text-muted-foreground">
                    No contact lists yet.
                  </TableCell>
                </TableRow>
              )}
              {lists?.map((r) => (
                <TableRow key={r.id} data-testid={`cl-row-${r.id}`}>
                  <TableCell>
                    <Link to={`/contact-lists/${r.id}`}
                          className="font-medium hover:underline text-primary"
                          data-testid={`cl-name-link-${r.id}`}>
                      {r.name}
                    </Link>
                    <div className="text-xs text-muted-foreground">{r.source_filename}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <span className="font-mono">
                      {r.row_stats.valid_rows} / {r.row_stats.total_rows}
                    </span>
                    {r.row_stats.duplicate_rows_dropped > 0 && (
                      <span className="ml-2 text-muted-foreground">
                        (-{r.row_stats.duplicate_rows_dropped} dup)
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusPill list={r} />
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.filterable_columns.join(", ") || "—"}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                      data-testid={`cl-open-${r.id}`}
                    >
                      <Link to={`/contact-lists/${r.id}`}>Open</Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => del(r)}
                      data-testid={`cl-delete-${r.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <UploadWizard
        projectId={id}
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onDone={() => {
          setWizardOpen(false);
          load();
        }}
      />
    </div>
  );
}

function StatusPill({ list }) {
  const s = list.status;
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (s !== "parsing") return;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/contact-lists/${list.id}/status`);
        if (!stopped) setProgress(data.progress);
      } catch (_e) {
        /* ignore */
      }
    };
    tick();
    const iv = setInterval(tick, 2000);
    return () => {
      stopped = true;
      clearInterval(iv);
    };
  }, [s, list.id]);

  if (s === "parsing")
    return (
      <div className="space-y-1">
        <Badge variant="secondary" className="text-[10px]">
          parsing
        </Badge>
        <div className="text-[10px] text-muted-foreground">
          {progress?.rows_processed ?? 0} rows
        </div>
      </div>
    );
  if (s === "failed") return <Badge variant="destructive">failed</Badge>;
  if (s === "ready") return <Badge variant="default">ready</Badge>;
  return <Badge variant="outline">{s}</Badge>;
}

function UploadWizard({ projectId, open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [phoneCol, setPhoneCol] = useState("");
  const [filterCols, setFilterCols] = useState({});
  const [dupPolicy, setDupPolicy] = useState("keep_first");
  const [cc, setCc] = useState("91");
  const [contactListId, setContactListId] = useState(null);
  const [status, setStatus] = useState(null);

  const reset = () => {
    setStep(1);
    setFile(null);
    setName("");
    setDesc("");
    setPreview(null);
    setPhoneCol("");
    setFilterCols({});
    setDupPolicy("keep_first");
    setCc("91");
    setContactListId(null);
    setStatus(null);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const doPreview = async () => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error("File too large (>100 MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/projects/${projectId}/contact-lists/preview`,
        fd,
      );
      setPreview(data);
      setName(name || file.name.replace(/\.(csv|xlsx|xlsm)$/i, ""));
      // Auto-detect a phone-like column
      const candidate = data.detected_columns.find((c) =>
        /^(mobile|phone|msisdn|number)$/i.test(c),
      );
      if (candidate) setPhoneCol(candidate);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doCreate = async () => {
    if (!phoneCol) {
      toast.error("Pick a phone column");
      return;
    }
    setBusy(true);
    try {
      const filterable = Object.entries(filterCols)
        .filter(([, v]) => v)
        .map(([k]) => k);
      const { data } = await api.post(`/projects/${projectId}/contact-lists`, {
        name,
        description: desc || undefined,
        gridfs_id: preview.gridfs_id,
        source_filename: preview.source_filename,
        source_bytes: preview.source_bytes,
        phone_column: phoneCol,
        filterable_columns: filterable,
        duplicate_policy: dupPolicy,
        country_code_default: cc,
      });
      setContactListId(data.id);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Poll status on step 3
  useEffect(() => {
    if (step !== 3 || !contactListId) return;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/contact-lists/${contactListId}/status`);
        if (stopped) return;
        setStatus(data);
        if (data.status === "ready") {
          toast.success(
            `Imported ${data.row_stats?.valid_rows ?? 0} of ${data.row_stats?.total_rows ?? 0} rows`,
          );
          onDone();
        } else if (data.status === "failed") {
          toast.error(`Parse failed: ${data.parse_error || "unknown error"}`);
        }
      } catch (_e) {
        /* ignore */
      }
    };
    tick();
    const iv = setInterval(tick, 2000);
    return () => {
      stopped = true;
      clearInterval(iv);
    };
  }, [step, contactListId, onDone]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-auto"
        data-testid="upload-wizard"
      >
        <DialogHeader>
          <DialogTitle>Upload contacts — step {step} of 3</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <FormRow label="Name">
              <Input
                data-testid="wizard-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. CAP Round 2026 leads"
              />
            </FormRow>
            <FormRow label="Description (optional)">
              <Textarea
                data-testid="wizard-desc"
                rows={2}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
              />
            </FormRow>
            <FormRow label="File (CSV / XLSX, max 100 MB)">
              <Input
                type="file"
                data-testid="wizard-file"
                accept=".csv,.xlsx,.xlsm"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file && (
                <div className="text-xs text-muted-foreground mt-1">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </FormRow>
            <DialogFooter>
              <Button
                onClick={doPreview}
                disabled={!file || busy}
                data-testid="wizard-next-1"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Next: preview
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground">
              Detected {preview.detected_columns.length} columns · showing first{" "}
              {preview.first_200_rows.length} rows · estimated total ≈{" "}
              {preview.row_count_estimate ?? "unknown"} rows
            </div>
            <div className="border rounded-md max-h-56 overflow-auto text-xs">
              <Table>
                <TableHeader>
                  <TableRow>
                    {preview.detected_columns.map((c) => (
                      <TableHead key={c}>{c}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.first_200_rows.slice(0, 20).map((r, i) => (
                    <TableRow key={i}>
                      {preview.detected_columns.map((c) => (
                        <TableCell key={c} className="font-mono text-[11px]">
                          {String(r[c] ?? "")}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-medium mb-2">
                  Phone column (required)
                </div>
                <RadioGroup value={phoneCol} onValueChange={setPhoneCol}>
                  <div className="space-y-1 max-h-40 overflow-auto pr-1">
                    {preview.detected_columns.map((c) => (
                      <div key={c} className="flex items-center gap-2">
                        <RadioGroupItem
                          value={c}
                          id={`phone-${c}`}
                          data-testid={`wizard-phonecol-${c}`}
                        />
                        <Label htmlFor={`phone-${c}`} className="text-xs cursor-pointer">
                          {c}
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              </div>
              <div>
                <div className="text-xs font-medium mb-2">Filterable columns</div>
                <div className="space-y-1 max-h-40 overflow-auto pr-1">
                  {preview.detected_columns
                    .filter((c) => c !== phoneCol)
                    .map((c) => (
                      <div key={c} className="flex items-center gap-2">
                        <Checkbox
                          id={`filter-${c}`}
                          data-testid={`wizard-filtercol-${c}`}
                          checked={!!filterCols[c]}
                          onCheckedChange={(v) =>
                            setFilterCols({ ...filterCols, [c]: !!v })
                          }
                        />
                        <Label htmlFor={`filter-${c}`} className="text-xs cursor-pointer">
                          {c}
                        </Label>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormRow label="Duplicate policy">
                <Select value={dupPolicy} onValueChange={setDupPolicy}>
                  <SelectTrigger data-testid="wizard-dup-policy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keep_first">Keep first (drop duplicates)</SelectItem>
                    <SelectItem value="keep_last">Keep last (replace)</SelectItem>
                    <SelectItem value="keep_all">Keep all (no dedup)</SelectItem>
                  </SelectContent>
                </Select>
              </FormRow>
              <FormRow label="Country code default">
                <Select value={cc} onValueChange={setCc}>
                  <SelectTrigger data-testid="wizard-cc">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="91">+91 (India)</SelectItem>
                  </SelectContent>
                </Select>
              </FormRow>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                onClick={doCreate}
                disabled={!phoneCol || busy}
                data-testid="wizard-start-import"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Start import
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="wizard-progress">
            <div className="text-sm">Parsing… you can close this window and come back.</div>
            <Progress value={Math.min(status?.progress?.percent ?? 5, 95)} />
            <div className="text-xs text-muted-foreground font-mono">
              rows processed: {status?.progress?.rows_processed ?? 0}
              {status?.status && <span> · status: {status.status}</span>}
            </div>
            {status?.status === "failed" && (
              <div className="text-xs text-destructive">
                {status?.parse_error}
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="wizard-close"
              >
                Close
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
